import { useState, useEffect, useMemo } from "react";
import DSBox from "./DSBox";
import DSTypography from "./DSTypography";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export interface DateTimePickerProps {
  selectedDate: Date;
  onChange: (date: Date) => void;
  minDate?: Date;
  isToday: (date: Date) => boolean;
}

const DateTimePicker = ({
  selectedDate,
  onChange,
  minDate = new Date(),
  isToday,
}: DateTimePickerProps) => {
  const [year, setYear] = useState(selectedDate.getFullYear());
  const [month, setMonth] = useState(selectedDate.getMonth());
  const [day, setDay] = useState(selectedDate.getDate());

  useEffect(() => {
    setYear(selectedDate.getFullYear());
    setMonth(selectedDate.getMonth());
    setDay(selectedDate.getDate());
  }, [selectedDate]);

  const currentDate = useMemo(
    () => new Date(year, month, day),
    [year, month, day],
  );
  const isTodaySelected = isToday(currentDate);

  const isCurrentYear = year === minDate.getFullYear();
  const isCurrentMonth = isCurrentYear && month === minDate.getMonth();
  const years = Array.from({ length: 6 }, (_, i) => minDate.getFullYear() + i);
  const months = MONTHS.map((name, index) => ({ name, index })).filter(
    ({ index }) => !isCurrentYear || index >= minDate.getMonth(),
  );

  const getDaysInMonth = (y: number, m: number) =>
    new Date(y, m + 1, 0).getDate();
  const minDay = isCurrentMonth ? minDate.getDate() : 1;
  const days = Array.from(
    { length: getDaysInMonth(year, month) - minDay + 1 },
    (_, i) => minDay + i,
  );

  const handleYearChange = (newYear: number) => {
    setYear(newYear);
    const newIsCurrentYear = newYear === minDate.getFullYear();
    let adjustedMonth = month;
    let adjustedDay = day;

    if (newIsCurrentYear && month < minDate.getMonth()) {
      adjustedMonth = minDate.getMonth();
      adjustedDay = minDate.getDate();
      setMonth(adjustedMonth);
      setDay(adjustedDay);
    }

    const newDate = new Date(newYear, adjustedMonth, adjustedDay, 0, 0, 0, 0);
    onChange(newDate);
  };

  const handleMonthChange = (newMonth: number) => {
    setMonth(newMonth);
    const newIsCurrentMonth = isCurrentYear && newMonth === minDate.getMonth();
    let adjustedDay = day;

    if (newIsCurrentMonth && day < minDate.getDate()) {
      adjustedDay = minDate.getDate();
      setDay(adjustedDay);
    }

    const newDate = new Date(year, newMonth, adjustedDay, 0, 0, 0, 0);
    onChange(newDate);
  };

  const handleDayChange = (newDay: number) => {
    setDay(newDay);

    const newDate = new Date(year, month, newDay, 0, 0, 0, 0);
    onChange(newDate);
  };

  const selectStyle = {
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    fontSize: "14px",
    minWidth: "120px",
    width: "100%",
  };

  return (
    <DSBox
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <DSBox
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 2,
          alignItems: "end",
        }}
      >
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <DSTypography sx={{ fontWeight: "bold" }} variant="caption">
            Year
          </DSTypography>
          <select
            value={year}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            style={selectStyle}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </DSBox>

        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <DSTypography sx={{ fontWeight: "bold" }} variant="caption">
            Month
          </DSTypography>
          <select
            value={month}
            onChange={(e) => handleMonthChange(Number(e.target.value))}
            style={selectStyle}
          >
            {months.map(({ name, index }) => (
              <option key={index} value={index}>
                {name}
              </option>
            ))}
          </select>
        </DSBox>

        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <DSTypography sx={{ fontWeight: "bold" }} variant="caption">
            Day
          </DSTypography>
          <select
            value={day}
            onChange={(e) => handleDayChange(Number(e.target.value))}
            style={selectStyle}
          >
            {days.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        </DSBox>
      </DSBox>

      <DSTypography
        sx={{
          fontWeight: "bold",
          color: isTodaySelected ? "primary.contrastText" : "text.primary",
          backgroundColor: isTodaySelected ? "primary.light" : "transparent",
          padding: isTodaySelected ? 1 : 0,
          borderRadius: 1,
          textAlign: "center",
        }}
        variant="body2"
      >
        {isTodaySelected
          ? `Selected: ${new Date(year, month, day).toLocaleDateString("en-GB")} - Today`
          : `Selected: ${new Date(year, month, day).toLocaleDateString("en-GB")}`}
      </DSTypography>
    </DSBox>
  );
};

export default DateTimePicker;
